import React, { useState } from 'react';
import { Building2, Phone, Mail, MapPin, Upload } from 'lucide-react';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { formatSiret } from '../../lib/utils';

interface BusinessFormProps {
    form: {
      company_name: string;
      phone_number: string;
      email: string;
      company_address: string;
      siret_number: string;
      legal_structure: string;
      area: string;
      logo?: string | null;
    };
    onChange: (form: any) => void;
    errors: { [key: string]: boolean };
  }
  
  const LEGAL_STRUCTURES = ['SARL', 'SAS', 'EURL', 'SA', 'SASU', 'Micro-entreprise', 'Autre'] as const;
  const REGIONS_FRANCE = [
    'Auvergne-Rhône-Alpes',
    'Bourgogne-Franche-Comté',
    'Bretagne',
    'Centre-Val de Loire',
    'Corse',
    'Grand Est',
    'Hauts-de-France',
    'Île-de-France',
    'Normandie',
    'Nouvelle-Aquitaine',
    'Occitanie',
    'Pays de la Loire',
    "Provence-Alpes-Côte d'Azur",
    'Guadeloupe',
    'Guyane',
    'La Réunion',
    'Martinique',
    'Mayotte'
  ] as const;
  
  export function BusinessForm({ form, onChange, errors }: BusinessFormProps) {
    const handleChange = (field: string, value: string) => {
      if (field === 'siret_number') {
        value = formatSiret(value);
      }
      onChange({ ...form, [field]: value });
    };
  
    const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          onChange({ ...form, logo: reader.result as string });
        };
        reader.readAsDataURL(file);
      }
    };
  
    const legalStructureOptions = LEGAL_STRUCTURES.map(structure => ({
      value: structure,
      label: structure
    }));
  
    const regionOptions = REGIONS_FRANCE.map(region => ({
      value: region,
      label: region
    }));
  
    return (
      <div className="space-y-6 animate-fadeIn">
        <div className="flex items-center gap-3 mb-8">
          <div className="p-2 bg-teal-50 rounded-lg">
            <Building2 className="h-6 w-6 text-teal-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900">
            Informations de l'entreprise
          </h2>
        </div>
  
        <div className="mb-8">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Logo de l'entreprise
          </label>
          <div className="flex items-center space-x-6">
            <div className="flex-shrink-0">
              {form.logo ? (
                <img
                  src={form.logo}
                  alt="Logo preview"
                  className="h-24 w-24 object-cover rounded-lg border border-gray-200"
                />
              ) : (
                <div className="h-24 w-24 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center bg-gray-50">
                  <Upload className="h-8 w-8 text-gray-400" />
                </div>
              )}
            </div>
            <div className="flex-1">
              <label
                htmlFor="logo-upload"
                className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
              >
                <Upload className="h-4 w-4 mr-2" />
                Choisir un logo
              </label>
              <input
                id="logo-upload"
                type="file"
                className="hidden"
                accept="image/*"
                onChange={handleLogoChange}
              />
              <p className="mt-2 text-xs text-gray-500">
                PNG, JPG jusqu'à 5MB
              </p>
            </div>
          </div>
        </div>
  
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nom de l'entreprise
          </label>
          <Input
            type="text"
            value={form.company_name}
            onChange={(e) => handleChange('company_name', e.target.value)}
            error={errors.company_name}
            className="py-2.5"
            required
          />
        </div>
  
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Téléphone
            </label>
            <Input
              type="tel"
              value={form.phone_number}
              onPhoneChange={(value) => handleChange('phone_number', value)}
              icon={<Phone className="h-5 w-5" />}
              error={errors.phone_number}
              className="py-2.5"
              required
            />
          </div>
  
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => handleChange('email', e.target.value)}
              icon={<Mail className="h-5 w-5" />}
              error={errors.email}
              className="py-2.5"
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              Cet email ne sera pas visible par les candidats
            </p>
          </div>
        </div>
  
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Adresse
          </label>
          <Input
            type="text"
            value={form.company_address}
            onChange={(e) => handleChange('company_address', e.target.value)}
            icon={<MapPin className="h-5 w-5" />}
            error={errors.company_address}
            className="py-2.5"
            required
          />
        </div>
  
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Numéro SIRET
            </label>
            <Input
              type="text"
              value={form.siret_number}
              onChange={(e) => handleChange('siret_number', e.target.value)}
              error={errors.siret_number}
              className="py-2.5"
              placeholder="123 456 789 00012"
              maxLength={18}
              required
            />
          </div>
  
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Structure juridique
            </label>
            <Select
              value={form.legal_structure}
              onChange={(e) => handleChange('legal_structure', e.target.value)}
              options={legalStructureOptions}
              error={errors.legal_structure}
              required
            />
          </div>
        </div>
  
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Région
          </label>
          <Select
            value={form.area}
            onChange={(e) => handleChange('area', e.target.value)}
            options={regionOptions}
            error={errors.area}
            required
          />
        </div>
      </div>
    );
  }